/** @jsxImportSource @emotion/react */
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material'
import { Popover, PopoverContent, PopoverTrigger } from '@app/components/ui/Popover/Popover'
import { availableClients, selectedClientAtom } from '@app/state/clientsAtom'
import { openChangeClientAtom } from '@app/state/layoutAtom'
import type { Client } from '@app/types'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  bodyStyles,
  cancelButton,
  confirmButton,
  contentStyles,
  footerStyles,
  headerStyles,
  headerTitle,
} from './ChangeClient.style'
import { toast } from '@app/components/ui/Toast/toast'
import { BASE_API_URL } from '@app/app-constants'
import { isLoadingAuthStateAtom } from '@app/state/authAtom'

const ChangeClient = () => {
  const clientList = useAtomValue(availableClients)
  const [open, setOpen] = useAtom(openChangeClientAtom)
  const selectedClient = useAtomValue(selectedClientAtom)
  const [tempClient, setTempClient] = useState<Client | null>(selectedClient)
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null)
  const setIsLoadingAuth = useSetAtom(isLoadingAuthStateAtom)

  useEffect(() => {
    setTempClient(selectedClient)
  }, [selectedClient])

  const handleConfirm = useCallback(async () => {
    if (!tempClient) return
    setIsLoadingAuth(true)

    toast({
      type: 'info',
      description: `Switching to ${tempClient.display_name}...`,
      position: 'top-center',
    })
    window.location.href = `${BASE_API_URL}/auth/login?client_name=${tempClient.subdomain}`
  }, [tempClient, setIsLoadingAuth])

  const handleCancel = useCallback(() => {
    setTempClient(selectedClient)
    setOpen(false)
  }, [selectedClient, setOpen])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span />
      </PopoverTrigger>

      <PopoverContent side="right" sideOffset={20} css={contentStyles}>
        <Box css={headerStyles}>
          <Typography variant="subtitle1" css={headerTitle}>
            Change Organisation
          </Typography>
        </Box>

        <Box css={bodyStyles}>
          <Autocomplete<Client>
            id="organization"
            fullWidth
            autoHighlight
            disabled={clientList.length === 0}
            value={tempClient}
            onChange={(_, newValue) => {
              setTempClient(newValue)
              if (newValue && confirmButtonRef.current) {
                confirmButtonRef.current.focus()
              }
            }}
            options={clientList}
            getOptionLabel={(option) => option.display_name}
            isOptionEqualToValue={(o, v) => o.client_name === v.client_name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organisation"
                variant="outlined"
                size="small"
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    !e.defaultPrevented &&
                    tempClient &&
                    tempClient.client_name !== selectedClient?.client_name &&
                    clientList.length > 0
                  ) {
                    e.preventDefault()
                    handleConfirm()
                  }
                }}
              />
            )}
          />
        </Box>

        <Box css={footerStyles}>
          <Button variant="text" size="small" onClick={handleCancel} css={cancelButton}>
            Cancel
          </Button>
          <Button
            ref={confirmButtonRef}
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={
              !tempClient ||
              clientList.length === 0 ||
              tempClient.client_name === selectedClient?.client_name
            }
            css={confirmButton}
          >
            Confirm
          </Button>
        </Box>
      </PopoverContent>
    </Popover>
  )
}

export default ChangeClient
